/**
 *
 * InstitutionLandingPage
 *
 */

import { scrollHandler } from 'utils';
import { useIntl } from 'react-intl';
import { useParams, useLocation, useHistory, Redirect } from 'react-router-dom';
import { useProduct, useFetch, useCart } from 'hooks';
import { useQueryParams, StringParam, withDefault } from 'use-query-params';
import React, { useEffect, useRef, useState } from 'react';

// Components
import { PublicPage } from 'components/institutions';
import { LandingPage } from 'components/landing';
import { LoadingScreen, CustomHelmet } from 'components/layouts';
import { SocialButtons } from 'components/common';
import { Mui } from 'components';

// Containers
import FamilyPurchaseButton from 'containers/families/FamilyPurchaseButton';
import FamilyEnrollmentSelect from 'containers/families/FamilyEnrollmentSelect';
import ThemeSensor from 'containers/widgets/ThemeSensor';

import * as api from './api';
import messages from './messages';

// Local components
import Analytics from './components/Analytics';
import MainProduct from './components/MainProduct';

export function InstitutionLandingPage() {
  // Hooks
  const intl = useIntl();
  const history = useHistory();
  const { pathname, state } = useLocation();
  const { landingPageSlug } = useParams();
  const { landingPage, retrieveProduct } = useProduct();
  const { transferTemporary, anonymousCart } = useCart();

  // States
  const [enrollment, setEnrollment] = useState(state?.enrollment);

  // Refs
  const primaryProductRef = useRef(null);

  // Queries
  const [query] = useQueryParams({
    o: withDefault(StringParam, ''),
  });

  // Fetch
  const { data: retrieve, error } = useFetch([api.endpoints.retrieve(landingPageSlug)], {
    dedupingInterval: 5 * 60 * 1000,
  });

  // Constants
  const { o: variantSlug } = query;
  const { institution = {}, showcase, title = '...', content = {} } = retrieve || {};

  // Effects

  useEffect(() => {
    if (variantSlug) retrieveProduct({ landingPageSlug }, variantSlug);
  }, [landingPageSlug, variantSlug, retrieveProduct]);

  // Actions
  const scrollToRef = ref => scrollHandler(ref.current.offsetTop - 60);

  const handleEnrollment = ({ enrollment: newEnrollment }) => {
    setEnrollment(newEnrollment);
    transferTemporary(newEnrollment);
    history.replace({ pathname, state: { enrollment: newEnrollment } });
  };

  const handleEnrollmentClear = () => setEnrollment(null);

  if (error) return <Redirect push to={{ pathname: '/404', search: `?origin=${pathname}` }} />;

  if (!retrieve) return <LoadingScreen />;

  return (
    <ThemeSensor institution={institution}>
      <CustomHelmet messages={messages} values={{ title }} />
      <Analytics landingPage={retrieve} institution={institution} />
      <FamilyEnrollmentSelect
        showcase={showcase}
        enrollment={enrollment}
        onSelect={handleEnrollment}
      />
      <LandingPage.Body backgroundColor="white">
        {content?.fullScreenHeroImage && (
          <LandingPage.Hero
            onClick={() => scrollToRef(primaryProductRef)}
            image={retrieve.heroImage}
            title={content.title}
            subtitle={content.subtitle}
            label={content.callToAction}>
            <LandingPage.Header institution={retrieve.institution} />
          </LandingPage.Hero>
        )}
        <SocialButtons.Share
          url={window?.location?.href}
          title={title}
          content={intl.formatMessage(messages.shareMessage)}
          hashtag="eskolare"
        />
        {!content?.disableFeatures && content?.features && (
          <Mui.Box my={3}>
            <LandingPage.Feature
              title={content.features.title}
              subtitle={content.features.subtitle}
              items={content.features.items}
            />
          </Mui.Box>
        )}
        <div ref={primaryProductRef}>
          {retrieve?.primaryProduct && (
            <Mui.Box my={8}>
              <LandingPage.SingleProduct>
                <MainProduct
                  enrollment={enrollment}
                  content={content}
                  product={retrieve.primaryProduct}
                  variant={landingPage[variantSlug]}
                  onCheckout={() => anonymousCart(true)}
                  onEnrollmentClear={handleEnrollmentClear}
                />
              </LandingPage.SingleProduct>
            </Mui.Box>
          )}
        </div>
        {retrieve?.secondaryProducts && (
          <Mui.Box my={8}>
            <LandingPage.MultiProduct
              title={content.header}
              subtitle={content.subheader}
              products={retrieve.secondaryProducts || []}
              to={newProduct => ({
                pathname: ['/l', landingPageSlug, 'p', newProduct?.slug].join('/'),
                state: { enrollment },
              })}
              PurchaseButton={newProduct => (
                <FamilyPurchaseButton product={newProduct} enrollment={enrollment} />
              )}
            />
          </Mui.Box>
        )}
        {!content?.disableFaq && content?.faq && (
          <LandingPage.Faq
            title={content.faq.title}
            subtitle={content.faq.subtitle}
            items={content.faq.items}
          />
        )}
        {retrieve?.institution && (
          <Mui.Box my={6}>
            <Mui.Container maxWidth="lg">
              <PublicPage.About institution={retrieve.institution} />
            </Mui.Container>
          </Mui.Box>
        )}
      </LandingPage.Body>
    </ThemeSensor>
  );
}

InstitutionLandingPage.propTypes = {};

export default InstitutionLandingPage;
